/* StylinArts Wishlist Page
 * - Loads wishlist from Cloudflare API
 * - Respects saved variant (colour/size) from PDP
 * - Uses variant image on the card
 * - Add to Bag uses the same variant
 */

(() => {
  "use strict";

  const API_BASE = "https://wishlist-api.stylinarts.com/_wishlist";
  const ROOT_SEL = "[data-sa-wishlist-page]";
  const GRID_SEL = "[data-sa-wishlist-grid]";
  const STATUS_SEL = "[data-sa-wishlist-status]";
  const EMPTY_SEL = "[data-sa-wishlist-empty]";
  const PREFS_KEY = "saWishlistPrefsV1";

  /* ---------- money + prefs helpers ---------- */

  function fmtMoney(cents) {
    try {
      if (typeof cents !== "number") return "";
      const currency =
        (window.Shopify &&
          Shopify.currency &&
          Shopify.currency.active) ||
        "AUD";
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function loadVariantPrefs() {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      return obj && typeof obj === "object" ? obj : {};
    } catch {
      return {};
    }
  }

  function getPrefForItem(meta, product) {
    const key =
      meta.product_id ||
      product.id ||
      meta.handle ||
      product.handle ||
      "";
    if (!key) return null;
    const prefs = loadVariantPrefs();
    return prefs[String(key)] || null;
  }

  /* ---------- API helpers ---------- */

  function normalizeItems(payload) {
    const items = Array.isArray(payload)
      ? payload
      : payload && Array.isArray(payload.items)
      ? payload.items
      : [];

    return items
      .map((it) => {
        if (typeof it === "string") return { handle: it };
        if (it && typeof it === "object") {
          return {
            handle: it.handle || it.product_handle || it.slug || "",
            product_id: it.product_id || it.productId || it.id || null
          };
        }
        return null;
      })
      .filter(Boolean)
      .filter((x) => x.handle);
  }

  async function apiGetWishlist() {
    const res = await fetch(API_BASE, { credentials: "include" });
    if (!res.ok) throw new Error(`Wishlist API GET failed (${res.status})`);
    return res.json();
  }

  async function apiToggle(item) {
    const body = {};
    if (item.product_id) body.product_id = item.product_id;
    if (item.handle) body.product_handle = item.handle;

    const res = await fetch(`${API_BASE}/toggle`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include"
    });

    if (!res.ok) throw new Error(`Wishlist API toggle failed (${res.status})`);
    return res.json().catch(() => ({}));
  }

  async function fetchProductByHandle(handle) {
    const res = await fetch(
      `/products/${encodeURIComponent(handle)}.js`,
      { credentials: "same-origin" }
    );
    if (!res.ok) throw new Error(`Product fetch failed for ${handle}`);
    return res.json();
  }

  /* ---------- UI helpers ---------- */

  function escapeHtml(str) {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function setStatus(root, msg) {
    const el = root.querySelector(STATUS_SEL);
    if (el) el.textContent = msg;
  }

  function setEmpty(root, isEmpty) {
    const empty = root.querySelector(EMPTY_SEL);
    const grid = root.querySelector(GRID_SEL);
    const status = root.querySelector(STATUS_SEL);
    if (empty) empty.style.display = isEmpty ? "" : "none";
    if (grid) grid.style.display = isEmpty ? "none" : "";
    if (status) status.style.display = isEmpty ? "none" : "";
  }

  function pickVariant(product, meta) {
    const pref = getPrefForItem(meta, product);

    let chosen = null;

    if (pref && pref.variant_id && Array.isArray(product.variants)) {
      const vid = Number(pref.variant_id);
      chosen =
        product.variants.find((v) => Number(v.id) === vid) || null;
    }

    if (!chosen && Array.isArray(product.variants) && product.variants.length) {
      chosen = product.variants[0];
    }

    return chosen;
  }

  function extractOption(variant, index) {
    if (!variant || !Array.isArray(variant.options)) return "";
    return variant.options[index] || "";
  }

  /* ---------- Card template (variant-aware image) ---------- */

function cardTemplate(product, meta) {
  const handle = product.handle || meta.handle;
  const url = product.url || (handle ? `/products/${handle}` : "#");
  const variant = pickVariant(product, meta);

  const variantId = variant ? Number(variant.id) : null;

  const price =
    variant && typeof variant.price === "number"
      ? fmtMoney(variant.price)
      : typeof product.price === "number"
      ? fmtMoney(product.price)
      : "";

  const colour = extractOption(variant, 0);
  const size = extractOption(variant, 1);

  const imgSrc =
    (variant &&
      variant.featured_image &&
      variant.featured_image.src) ||
    (product.featured_image && product.featured_image.src) ||
    (product.images && product.images[0]) ||
    "";

  const colourLabel = colour ? escapeHtml(colour) : "";
  const sizeLabel = size ? escapeHtml(size) : "";

  // 🔸 NEW: use the raw colour label as CSS variable (handles “Black”, “#f1e5d8”, etc.)
  const swatchColor = colour ? colour.toLowerCase() : "";

  return `
    <article class="sa-wish-card"
             data-sa-wish-handle="${escapeHtml(handle)}"
             data-product-id="${meta.product_id || ""}">
      <a class="sa-wish-card__media" href="${url}" aria-label="${escapeHtml(
        product.title || handle || "Product"
      )}">
        ${
          imgSrc
            ? `<img class="sa-wish-card__img"
                    src="${imgSrc}"
                    alt="${escapeHtml(product.title || handle || "Product")}"
                    loading="lazy">`
            : `<div class="sa-wish-card__ph"></div>`
        }
      </a>

      <div class="sa-wish-card__body">
        <a class="sa-wish-card__title" href="${url}">
          ${escapeHtml(product.title || handle || "Product")}
        </a>

        <div class="sa-wish-card__meta">
          <span class="sa-wish-card__price">${price}</span>
          ${
            colourLabel || sizeLabel
              ? `<span class="sa-wish-card__variant">
                   ${
                     colourLabel
                       ? `<span class="sa-wish-card__swatch"
                                style="--sa-swatch-color:${escapeHtml(
                                  swatchColor
                                )};"
                                title="${colourLabel}"></span>`
                       : ""
                   }
                   ${
                     sizeLabel
                       ? `<span class="sa-wish-card__size">${sizeLabel}</span>`
                       : ""
                   }
                 </span>`
              : ""
          }
        </div>

        <div class="sa-wish-card__actions">
          <button type="button"
            class="sa-wish-card__add"
            data-sa-wish-add
            data-variant-id="${variantId || ""}">
            Add to bag
          </button>

          <button type="button"
            class="sa-wish-card__remove"
            data-sa-wish-remove>
            Remove
          </button>
        </div>
      </div>
    </article>
  `;
}


  /* ---------- Main render ---------- */

  async function render(root) {
    const grid = root.querySelector(GRID_SEL);
    if (!grid) return;

    setEmpty(root, false);
    setStatus(root, "Loading your wishlist…");
    grid.innerHTML = "";

    let items = [];
    try {
      const payload = await apiGetWishlist();
      items = normalizeItems(payload);
    } catch (e) {
      console.error(e);
      setStatus(root, "Couldn’t load wishlist right now. Please refresh.");
      return;
    }

    if (!items.length) {
      setEmpty(root, true);
      return;
    }

    setStatus(
      root,
      `Showing ${items.length} saved item${items.length === 1 ? "" : "s"}.`
    );

    const results = await Promise.allSettled(
      items.map((it) => fetchProductByHandle(it.handle))
    );

    const cards = [];
    results.forEach((r, idx) => {
      const meta = items[idx];
      if (r.status === "fulfilled") {
        cards.push(cardTemplate(r.value, meta));
      } else {
        cards.push(`
          <article class="sa-wish-card sa-wish-card--missing" data-sa-wish-handle="${escapeHtml(
            meta.handle
          )}">
            <div class="sa-wish-card__media"><div class="sa-wish-card__ph"></div></div>
            <div class="sa-wish-card__body">
              <div class="sa-wish-card__title sa-wish-card__title--dead">${escapeHtml(
                meta.handle
              )}</div>
              <div class="sa-wish-card__meta">
                <span class="sa-wish-card__price">Unavailable</span>
              </div>
              <div class="sa-wish-card__actions">
                <button type="button"
                  class="sa-wish-card__btn sa-wish-card__btn--ghost"
                  data-sa-wish-remove>
                  Remove
                </button>
              </div>
            </div>
          </article>
        `);
      }
    });

    grid.innerHTML = cards.join("");

    bindEvents(root);
  }

  /* ---------- Events: remove + add-to-bag ---------- */

  function bindEvents(root) {
    const grid = root.querySelector(GRID_SEL);
    if (!grid) return;

    grid.addEventListener(
      "click",
      async (ev) => {
        const removeBtn = ev.target.closest("[data-sa-wish-remove]");
        const addBtn = !removeBtn && ev.target.closest("[data-sa-wish-add]");

        if (removeBtn) {
          const card = removeBtn.closest(".sa-wish-card");
          if (!card) return;
          const handle = card.getAttribute("data-sa-wish-handle") || "";
          const productIdRaw = card.getAttribute("data-product-id") || "";
          const product_id = productIdRaw ? Number(productIdRaw) : null;

          removeBtn.disabled = true;
          removeBtn.classList.add("is-loading");

          try {
            await apiToggle({ handle, product_id });
            card.remove();

            const remaining = grid.querySelectorAll(".sa-wish-card").length;
            if (!remaining) {
              setEmpty(root, true);
            } else {
              setStatus(
                root,
                `Showing ${remaining} saved item${
                  remaining === 1 ? "" : "s"
                }.`
              );
            }

            if (typeof window.saWishlistHeaderRefresh === "function") {
              window.saWishlistHeaderRefresh();
            }
          } catch (e) {
            console.error(e);
            removeBtn.disabled = false;
            removeBtn.classList.remove("is-loading");
            setStatus(root, "Couldn’t update wishlist. Please try again.");
          }
          return;
        }

        if (addBtn) {
          const variantRaw = addBtn.getAttribute("data-variant-id") || "";
          const variantId = variantRaw ? Number(variantRaw) : null;
          if (!variantId) return;

          addBtn.disabled = true;
          addBtn.classList.add("is-loading");

          try {
            await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json"
              },
              credentials: "same-origin",
              body: JSON.stringify({
                items: [{ id: variantId, quantity: 1 }]
              })
            });

            // If you have a cart drawer opener, hook it here:
            if (typeof window.openCartDrawer === "function") {
              window.openCartDrawer();
            }

            addBtn.classList.remove("is-loading");
            addBtn.disabled = false;
          } catch (e) {
            console.error(e);
            addBtn.classList.remove("is-loading");
            addBtn.disabled = false;
            setStatus(root, "Couldn’t add to bag. Please try again.");
          }
        }
      },
      { passive: true }
    );
  }

  /* ---------- Boot ---------- */

  function boot() {
    const root = document.querySelector(ROOT_SEL);
    if (!root) return;
    render(root);
  }

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:section:select", boot);
})();



